import {
    PHOTOGRAMMETRY_JOB_NAME,
    PREEXEC_ACTION_KINDS,
    RAW_DATASET_TYPES,
} from "../../constants";

function filterNullishProperties(inputObject) {
    return Object.fromEntries(
        Object.entries(inputObject).filter(([, value]) => value != null)
    );
}

export class PipelineInstanceDownloader {
    constructor(pipelineInstance) {
        this.pipelineInstance = pipelineInstance;
    }

    get #photogrammetryPreexecItems() {
        const photogrammetryJob =
            this.pipelineInstance.translatedPipeline?.spec.jobs.find(
                (job) => job.name === PHOTOGRAMMETRY_JOB_NAME
            );

        if (!photogrammetryJob) return undefined;

        const items = JSON.parse(photogrammetryJob.preExec.args[0]);
        delete items.processUuid;
        delete items.outputFolder;

        return items;
    }

    #photogrammetryBinaryFile(outputBinaryDataName) {
        return {
            kind: PREEXEC_ACTION_KINDS.DOWNLOAD_FILE,
            endpoint: `/pipeline-instances/${this.pipelineInstance.uuid}/output-binary-data`,
            args: {
                name: outputBinaryDataName,
                stepName: PHOTOGRAMMETRY_JOB_NAME,
            },
            from: "s3Location",
            to: "relativePath",
        };
    }

    get missionUuid() {
        return this.pipelineInstance.translateParams?.variables?.missionUuid;
    }

    /**
     * Panel images of the mission
     */
    get panelImages() {
        return this.missionUuid
            ? {
                  kind: PREEXEC_ACTION_KINDS.DOWNLOAD_FILES,
                  endpoint: "/raw-datasets",
                  args: {
                      missionUuid: this.missionUuid,
                      type: RAW_DATASET_TYPES.PANEL_IMAGE,
                      quarantine: false,
                  },
                  from: "s3Location",
                  to: "destination",
              }
            : undefined;
    }

    /**
     * Photogrammetry inputs
     */
    get cameraProfile() {
        return this.#photogrammetryPreexecItems?.downloadCameraProfile;
    }
    // The RGB images can come from either fusion or image undistort
    get imagesRgb() {
        if (!this.#photogrammetryPreexecItems) return undefined;

        return (
            this.#photogrammetryPreexecItems.downloadMergedRawDatasets ??
            this.#photogrammetryPreexecItems.downloadUndistortedImages
        );
    }
    // There can be MS images (in the case of the RGB+MS M3M pipeline for example)
    get imagesMs() {
        return this.#photogrammetryPreexecItems?.downloadRegisteredImages;
    }
    get gcpFile() {
        return this.#photogrammetryPreexecItems?.downloadGcpFile;
    }

    /**
     * Photogrammetry outputs
     */
    get geofolder() {
        return this.#photogrammetryBinaryFile("geofolder");
    }
    get metashape() {
        return this.#photogrammetryBinaryFile("metashape");
    }
    get detectedTargetsCsv() {
        return this.#photogrammetryBinaryFile("detectedTargets");
    }
    get orthomosaicRgb() {
        return this.#photogrammetryBinaryFile("orthomosaicRgb");
    }
    get orthomosaicRgb10mm() {
        return this.#photogrammetryBinaryFile("orthomosaicRgb10mm");
    }
    get photogrammetryLog() {
        return this.#photogrammetryBinaryFile("log");
    }

    /**
     * Session metadata JSON
     */
    get sessionMetadataJson() {
        return this.missionUuid
            ? {
                  kind: PREEXEC_ACTION_KINDS.BUILD_SESSION_METADATA,
                  missionUuid: this.missionUuid,
                  to: "session-metadata.json",
              }
            : undefined;
    }

    get allDownloadableItems() {
        return filterNullishProperties({
            cameraProfile: this.cameraProfile,
            imagesRgb: this.imagesRgb,
            imagesMs: this.imagesMs,
            gcpFile: this.gcpFile,
            geofolder: this.geofolder,
            metashape: this.metashape,
            detectedTargetsCsv: this.detectedTargetsCsv,
            orthomosaicRgb: this.orthomosaicRgb,
            orthomosaicRgb10mm: this.orthomosaicRgb10mm,
            photogrammetryLog: this.photogrammetryLog,
            panelImages: this.panelImages,
            sessionMetadataJson: this.sessionMetadataJson,
        });
    }

    get itemsForExtraction() {
        return filterNullishProperties({
            cameraProfile: this.cameraProfile,
            imagesRgb: this.imagesRgb,
            imagesMs: this.imagesMs,
            geofolder: this.geofolder,
            orthomosaicRgb: this.orthomosaicRgb,
            orthomosaicRgb10mm: this.orthomosaicRgb10mm,
            panelImages: this.panelImages,
            sessionMetadataJson: this.sessionMetadataJson,
        });
    }

    get itemsForPhotogrammetry() {
        return this.allDownloadableItems;
    }
}
